import React from 'react';
import { Shield, Award, Users, Target, Eye, Heart, CheckCircle, TrendingUp } from 'lucide-react';
import { companyInfo } from '../mockData';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">About Vetam Remedies</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              A trusted pharmaceutical company committed to providing quality medicines at the lowest market prices, making healthcare accessible to all.
            </p>
          </div>
        </div>
      </div>

      {/* Company Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Who We Are
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  <strong className="text-blue-600">{companyInfo.name}</strong> is a leading pharmaceutical company in association with <strong>WALTER®</strong>, dedicated to delivering high-quality medicines to healthcare professionals, retailers, and patients across India.
                </p>
                <p>
                  We operate under strategic partnerships with WHO-GMP certified manufacturing plants, ensuring that every product meets the highest standards of efficacy, safety, and quality.
                </p>
                <p>
                  Our comprehensive range covers multiple therapeutic areas, from gastrointestinal health to women's care, antibiotics to nutritional supplements - all offered at the most competitive prices in the market.
                </p>
              </div>
              
              <div className="mt-8 grid grid-cols-2 gap-4">
                {companyInfo.certifications.map((cert, index) => (
                  <div key={index} className="bg-gradient-to-br from-blue-50 to-white p-4 rounded-xl border-2 border-blue-200">
                    <Award className="w-8 h-8 text-blue-600 mb-2" />
                    <p className="font-semibold text-gray-900">{cert}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-10 -right-10 w-72 h-72 bg-blue-200 rounded-full blur-3xl opacity-30"></div>
              <img
                src="\images\about_img2.png"
                alt="Pharmaceutical Laboratory"
                className="relative rounded-2xl shadow-2xl w-full h-auto"
                style={{ filter: 'drop-shadow(0 0 25px rgba(59, 130, 246, 0.35))' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {companyInfo.mission}
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl flex items-center justify-center mb-6">
                  <Eye className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {companyInfo.vision}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {companyInfo.values.map((value, index) => (
              <Card key={index} className="border-2 hover:shadow-xl transition-all hover:-translate-y-2 group">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <CheckCircle className="w-7 h-7 text-white" />
                  </div>
                  <p className="text-gray-900 font-semibold leading-relaxed">{value}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Partner with Us
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover the advantages of choosing Vetam Remedies as your pharmaceutical partner
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Quality Assurance</h3>
                <p className="text-gray-700 leading-relaxed">
                  WHO-GMP certified manufacturing with stringent quality control measures at every stage of production.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Competitive Pricing</h3>
                <p className="text-gray-700 leading-relaxed">
                  Lowest market prices without compromising on quality, making healthcare affordable for all.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center mb-4">
                  <Award className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Certified Excellence</h3>
                <p className="text-gray-700 leading-relaxed">
                  ISO and GLP certified facilities meeting national and international accreditation standards.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-700 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Extensive Network</h3>
                <p className="text-gray-700 leading-relaxed">
                  Wide distribution network ensuring timely delivery and availability across regions.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-700 rounded-xl flex items-center justify-center mb-4">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Patient-Centric</h3>
                <p className="text-gray-700 leading-relaxed">
                  Focused on improving patient outcomes with effective and safe pharmaceutical solutions.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Trusted by Professionals</h3>
                <p className="text-gray-700 leading-relaxed">
                  Preferred choice of doctors and healthcare professionals for reliable medication.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Manufacturing Standards */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-3xl p-12 text-white">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Manufacturing Excellence
              </h2>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                {companyInfo.manufacturing}
              </p>
              <div className="grid md:grid-cols-3 gap-6 mt-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h4 className="font-bold text-2xl mb-2">WHO-GMP</h4>
                  <p className="text-blue-100">Certified Manufacturing</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h4 className="font-bold text-2xl mb-2">ISO</h4>
                  <p className="text-blue-100">Quality Management</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h4 className="font-bold text-2xl mb-2">GLP</h4>
                  <p className="text-blue-100">Laboratory Practices</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Office */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Corporate Office
            </h2>
          </div>
          <Card className="max-w-4xl mx-auto border-2">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">{companyInfo.name}</h3>
                  <div className="space-y-4 text-gray-700">
                    <p className="leading-relaxed">
                      <strong className="text-gray-900">Address:</strong><br />
                      {companyInfo.corporateOffice.address}
                    </p>
                    <p>
                      <strong className="text-gray-900">Phone:</strong><br />
                      <a href={`tel:${companyInfo.corporateOffice.phone}`} className="text-blue-600 hover:text-blue-700">
                        {companyInfo.corporateOffice.phone}
                      </a>
                    </p>
                    <p>
                      <strong className="text-gray-900">Email:</strong><br />
                      <a href={`mailto:${companyInfo.corporateOffice.email}`} className="text-blue-600 hover:text-blue-700 break-all">
                        {companyInfo.corporateOffice.email}
                      </a>
                    </p>
                  </div>
                </div>
                <div className="relative h-64 md:h-auto">
                  <img
                    src="\images\about_img.png"
                    alt="Corporate Office"
                    className="w-full h-full object-cover rounded-xl"
                    style={{ filter: 'drop-shadow(0 0 15px rgba(59, 130, 206, 0.35))' }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default About;
